/**
 * ユーザー一覧(一覧テンプレート: ページヘッダー / ビューと条件 / 表 / 右パネル)。
 *
 * **正は Keycloak**。スイートの表に持っているのは最終ログインだけ。
 * 条件(`q` / `enabled`)と右パネル(`panel`)は URL の search params に載せる。
 * 行を押すと右パネル(`UserPanel`)、「全画面で開く」で `/idp/users/:id`(`UserDetailPage`)。
 *
 * 一覧 API(`GET /api/idp/users`)には MFA の有無とグループ数が無いので、列は
 * ポリシーで代える(右パネル・詳細では MFA を出す)。
 */
import { useQueries } from "@tanstack/react-query";
import { Alert, Button } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { IdpUser } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  StatusTag,
  TimeAgo,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { AddUserModal } from "./AddUserModal";
import { errorText, useIdpDownload, useIdpPerms, useIdpUsers } from "./hooks";
import { ImportModal } from "./ImportModal";
import { UserPanel } from "./UserPanel";
import { USER_COLUMN_LABEL, userStatusTone } from "./userOptions";

const VIEWS = [
  { key: "all", label: "すべて", enabled: "" },
  { key: "enabled", label: "有効", enabled: "true" },
  { key: "disabled", label: "無効", enabled: "false" },
] as const;

/** ビューの件数(検索語は掛けない。チップは母数を示す)。 */
function useViewCounts(): Record<string, number | undefined> {
  const api = useApi();
  const results = useQueries({
    queries: VIEWS.map((v) => ({
      queryKey: ["idp", "users", "count", v.key],
      queryFn: () =>
        api.idpUsers(new URLSearchParams({ limit: "1", offset: "0", ...(v.enabled ? { enabled: v.enabled } : {}) })),
      select: (d: { total: number }) => d.total,
      staleTime: 60_000,
    })),
  });
  return Object.fromEntries(VIEWS.map((v, i) => [v.key, results[i].data]));
}

export function UsersPage() {
  const { canAdmin } = useIdpPerms();
  const download = useIdpDownload();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const filters = { q: sp.get("q") ?? "", enabled: sp.get("enabled") ?? "" };

  // 検索語は打鍵ごとに投げない。落ち着いてから URL に載せる(履歴は増やさない)
  const [qInput, setQInput] = useSearchParamInput("q");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const counts = useViewCounts();

  const query = useIdpUsers({ ...filters, limit: pageSize, offset: (page - 1) * pageSize });
  useEffect(() => {
    setPage(1);
  }, [filters.q, filters.enabled]);

  const view = VIEWS.find((v) => v.enabled === filters.enabled)?.key ?? "all";

  const columns: DataColumn<IdpUser>[] = [
    {
      title: USER_COLUMN_LABEL.displayName,
      ellipsis: false,
      dataIndex: "displayName",
      width: 240,
      // 行クリックと併存。キーボード(Tab → Enter)でもパネルを開けるように
      render: (v: string, row) => (
        <button type="button" className="ui-link-btn ui-cell-main" onClick={() => setPanelId(row.id)}>
          {v || row.username}
        </button>
      ),
    },
    {
      title: USER_COLUMN_LABEL.username,
      ellipsis: false,
      dataIndex: "username",
      width: 240,
      render: (v: string) => <span className="ui-num">{v}</span>,
    },
    { title: USER_COLUMN_LABEL.email, ellipsis: false, dataIndex: "email", width: 240 },
    {
      title: USER_COLUMN_LABEL.status,
      ellipsis: false,
      dataIndex: "status",
      width: 110,
      render: (v: string) => (
        <StatusTag tone={userStatusTone(v)} dot>
          {v}
        </StatusTag>
      ),
    },
    {
      // 効いているアクセスポリシー(R7d)。一覧の行に入っているので追加の問い合わせはしない
      title: USER_COLUMN_LABEL.policy,
      ellipsis: false,
      dataIndex: "policyName",
      width: 200,
      render: (v: string, row) =>
        v ? (
          <>
            {v}
            {row.policyIsDefault && <span className="ui-cell-sub"> 既定</span>}
          </>
        ) : (
          <StatusTag tone="warning">未割当</StatusTag>
        ),
    },
    {
      title: USER_COLUMN_LABEL.lastLogin,
      ellipsis: false,
      dataIndex: "lastLogin",
      width: 160,
      render: (v: string | null) => <TimeAgo iso={v} />,
    },
  ];

  const filtered = !!(filters.q || filters.enabled);
  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="ユーザー一覧"
          description="Keycloak のユーザー。追加・編集はここから送り、正は Keycloak が持つ。"
          secondary={
            <>
              <Button onClick={() => void download("/api/idp/users/export.csv")}>CSV エクスポート</Button>
              {canAdmin && <Button onClick={() => setImporting(true)}>一括登録</Button>}
            </>
          }
          primary={
            canAdmin ? (
              <Button type="primary" onClick={() => setAdding(true)}>
                ユーザーの追加
              </Button>
            ) : undefined
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "ユーザー名・氏名・メールで探す" }}
          views={{
            items: VIEWS.map((v) => ({ key: v.key, label: v.label, count: counts[v.key] })),
            active: view,
            onChange: (key) => patch({ enabled: VIEWS.find((v) => v.key === key)?.enabled || null }),
          }}
        />
        {query.isError && (
          <Alert type="error" showIcon message="ユーザーを取得できなかった" description={errorText(query.error)} />
        )}
        <DataTable<IdpUser>
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={query.data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          unit="人"
          selectedKey={panelId || null}
          onRowClick={(row) => setPanelId(row.id)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: filtered ? "条件に合うユーザーはいない" : "ユーザーがまだいない",
            action: filtered ? (
              <Button
                onClick={() => {
                  setQInput("");
                  patch({ q: null, enabled: null });
                }}
              >
                条件を外す
              </Button>
            ) : canAdmin ? (
              <Button type="primary" onClick={() => setAdding(true)}>
                ユーザーの追加
              </Button>
            ) : undefined,
          }}
        />
      </div>
      {panelId && <UserPanel id={panelId} onClose={() => setPanelId(null)} />}
      <AddUserModal open={adding} onClose={() => setAdding(false)} />
      <ImportModal open={importing} onClose={() => setImporting(false)} />
    </div>
  );
}
